'use client';

import type { ReactNode } from 'react';
import { Lock, Infinity, Star } from 'lucide-react';
import { MaskedSvgIcon } from '@/components/help-center/MaskedSvgIcon';
import { MoXat, cn } from '@/components/help-center/latest-power-shared';
import { getPowerCategoryBadgeStyle } from '@/lib/power-category-theme';
import { formatStorePrice } from '@/lib/powers-data';

/** Chave de cor: categoria canónica de `/powers`, com fallback legado por type. */
export function fairtradeCategoryKey(typeOrCategory: string): string {
  const t = typeOrCategory.trim().toLowerCase();
  if (
    t === 'smiley' ||
    t === 'function' ||
    t === 'group' ||
    t === 'hug' ||
    t === 'stickers' ||
    t === 'games' ||
    t === 'epic' ||
    t === 'collection' ||
    t === 'jinx'
  ) {
    return t;
  }
  if (t === 'yellow') return 'limited';
  if (t.startsWith('super')) return 'collection';
  if (t === 'hugs') return 'hug';
  if (t === 'jinxes') return 'jinx';
  if (t === 'kao' || t === 'animal' || t === 'other') return 'smiley';
  if (t === 'backs' || t === 'back') return 'group';
  if (t === 'colors' || t === 'color') return 'epic';
  if (t === 'holiday' || t === 'santa' || t === 'event') return 'games';
  if (t === 'smilies') return 'smiley';
  return t;
}

export function isFairtradeCollectionType(type: string): boolean {
  return type.trim().toLowerCase().startsWith('super');
}

export function FairtradeCategoryBadge({
  type,
  typeLabel,
  category,
}: {
  type: string;
  typeLabel: string;
  /** Preferir categoria canónica de `/powers` quando disponível. */
  category?: string;
}) {
  const style = getPowerCategoryBadgeStyle(fairtradeCategoryKey(category || type));

  return (
    <span
      className="inline-flex rounded-full border px-2.5 py-0.5 font-poppins text-[10px] font-semibold uppercase tracking-wide"
      style={style}
    >
      {typeLabel}
    </span>
  );
}

export function FairtradeAvailabilityBadge({
  limited,
  isDark = false,
}: {
  limited: boolean;
  isDark?: boolean;
}) {
  if (limited) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-widest text-white"
        style={{
          background: 'linear-gradient(90deg, #e8620a 0%, #f47c1a 50%, #e8a020 100%)',
          boxShadow: '0 0 0 1.5px rgba(240,130,30,0.40), 0 4px 12px rgba(220,90,10,0.30)',
        }}
      >
        <Lock className="h-3 w-3" aria-hidden />
        Limitado
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-cyan-500/30 to-sky-500/30 px-2.5 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-widest ring-1 ring-cyan-400/40',
        isDark ? 'text-cyan-300' : 'text-cyan-700'
      )}
    >
      <Infinity className="h-3 w-3" aria-hidden />
      Ilimitado
    </span>
  );
}

/** Badge “Novo” do power mais recente na lista Fairtrade. */
export function FairtradeNewBadge({ isDark = false }: { isDark?: boolean }) {
  return (
    <span
      className={cn(
        'relative inline-flex h-5 shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full px-2',
        'font-poppins text-[9px] font-bold uppercase leading-none tracking-wider',
        isDark
          ? 'bg-rose-500/25 text-rose-50 ring-1 ring-rose-400/50'
          : 'bg-rose-100 text-rose-800 ring-1 ring-rose-300/80'
      )}
    >
      <span
        className="fairtrade-new-badge-shimmer pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-18deg] bg-gradient-to-r from-transparent via-white/55 to-transparent"
        aria-hidden
      />
      <MaskedSvgIcon
        src="/icons/power.svg"
        className={cn(
          'relative h-3 w-3 shrink-0',
          isDark ? 'text-rose-100' : 'text-rose-700'
        )}
      />
      <span className="relative leading-none">Novo</span>
    </span>
  );
}

export function FairtradeIdBadge({ id, isDark = false }: { id: number; isDark?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 font-poppins text-[10px] font-bold tabular-nums',
        isDark
          ? 'border-blue-800/80 bg-blue-950/60 text-sky-200'
          : 'border-slate-200 bg-slate-50 text-slate-600'
      )}
    >
      {id}
    </span>
  );
}

export function DaysStarIcon({ className }: { className?: string }) {
  return (
    <Star
      className={cn('h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400', className)}
      aria-hidden
    />
  );
}

/** Separador horizontal min–máx — fino, visível em dark e light. */
function FairtradeRangeSeparator({ isDark = false }: { isDark?: boolean }) {
  return (
    <span
      role="separator"
      aria-hidden
      className="mx-1 inline-block h-px w-3 shrink-0 self-center rounded-full"
      style={{
        background: isDark
          ? 'linear-gradient(to right, transparent 0%, rgba(186,230,253,0.7) 50%, transparent 100%)'
          : 'linear-gradient(to right, transparent 0%, rgba(71,85,105,0.75) 50%, transparent 100%)',
      }}
    />
  );
}

function FairtradeRangeAmount({
  min,
  max,
  isDark = false,
}: {
  min: number;
  max: number;
  isDark?: boolean;
}) {
  if (!min && !max) return null;
  if (min === max || (!min && max) || (min && !max)) {
    return <span>{min || max}</span>;
  }
  return (
    <span className="inline-flex items-center gap-0.5 tabular-nums">
      <span>{min}</span>
      <FairtradeRangeSeparator isDark={isDark} />
      <span>{max}</span>
    </span>
  );
}

export function FairtradeXatsValue({
  min,
  max,
  className,
  isDark = false,
}: {
  min: number;
  max: number;
  className?: string;
  isDark?: boolean;
}) {
  const hasValue = min > 0 || max > 0;
  if (!hasValue) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 font-poppins text-sm font-medium',
          isDark ? '!text-blue-200/70' : '!text-gray-500',
          className
        )}
      >
        <MoXat className="h-3.5 w-3.5 opacity-70" />
        A definir
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex min-w-0 max-w-full flex-nowrap items-center gap-1.5 whitespace-nowrap font-poppins text-sm font-semibold tabular-nums',
        isDark ? '!text-sky-50' : '!text-gray-900',
        className
      )}
    >
      <MoXat className="h-3.5 w-3.5 shrink-0" />
      <FairtradeRangeAmount min={min} max={max} isDark={isDark} />
      <span
        className={cn(
          'shrink-0 font-poppins text-xs font-medium',
          isDark ? '!text-blue-200/70' : '!text-gray-500'
        )}
      >
        xats
      </span>
    </span>
  );
}

export function FairtradeDaysValue({
  min,
  max,
  className,
  isDark = false,
}: {
  min: number;
  max: number;
  className?: string;
  isDark?: boolean;
}) {
  const hasValue = min > 0 || max > 0;
  if (!hasValue) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 font-poppins text-sm font-medium',
          isDark ? '!text-blue-200/70' : '!text-gray-500',
          className
        )}
      >
        <DaysStarIcon className="h-3.5 w-3.5 opacity-70" />
        A definir
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex min-w-0 max-w-full flex-nowrap items-center gap-1.5 whitespace-nowrap font-poppins text-sm font-semibold tabular-nums',
        isDark ? '!text-sky-50' : '!text-gray-900',
        className
      )}
    >
      <DaysStarIcon className="h-3.5 w-3.5 shrink-0" />
      <FairtradeRangeAmount min={min} max={max} isDark={isDark} />
      <span
        className={cn(
          'shrink-0 font-poppins text-xs font-medium',
          isDark ? '!text-blue-200/70' : '!text-gray-500'
        )}
      >
        days
      </span>
    </span>
  );
}

export function FairtradeStoreValue({
  storePrice,
  className,
  isDark = false,
  accent = false,
}: {
  storePrice: string;
  className?: string;
  isDark?: boolean;
  /** Destaque visual distinto (loja vs fairtrade). */
  accent?: boolean;
}) {
  const formatted = formatStorePrice(storePrice || '0');
  const muted = accent
    ? isDark
      ? '!text-violet-200/70'
      : '!text-violet-600/80'
    : isDark
      ? '!text-blue-200/70'
      : '!text-gray-500';
  const strong = accent
    ? isDark
      ? '!text-violet-100'
      : '!text-violet-800'
    : isDark
      ? '!text-sky-50'
      : '!text-gray-900';

  const wrap = (children: ReactNode) =>
    accent ? (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg border px-2 py-1',
          isDark
            ? 'border-violet-400/25 bg-violet-500/10'
            : 'border-violet-200/90 bg-violet-50',
          className
        )}
      >
        {children}
      </span>
    ) : (
      <span className={className}>{children}</span>
    );

  if (formatted === 'Gratuito' || formatted === 'Não comercializável') {
    return wrap(
      <span
        className={cn(
          'font-poppins text-sm font-semibold',
          accent
            ? strong
            : isDark
              ? '!text-blue-100/85'
              : '!text-gray-700'
        )}
      >
        {formatted}
      </span>
    );
  }

  if (/days?/i.test(formatted)) {
    const amount = formatted.replace(/\s*days?/i, '').trim();
    return wrap(
      <span
        className={cn(
          'inline-flex items-center gap-1.5 font-poppins text-sm font-semibold',
          strong
        )}
      >
        <DaysStarIcon className="h-3.5 w-3.5 shrink-0" />
        <span>{amount}</span>
        <span className={cn('font-poppins text-xs font-medium', muted)}>days</span>
      </span>
    );
  }

  const amount = formatted.replace(/\s*xats$/i, '').trim();
  return wrap(
    <span
      className={cn(
        'inline-flex items-center gap-1.5 font-poppins text-sm font-semibold',
        strong
      )}
    >
      <MoXat className="h-3.5 w-3.5 shrink-0" />
      <span>{amount}</span>
      <span className={cn('font-poppins text-xs font-medium', muted)}>xats</span>
    </span>
  );
}
